import { useCallback, useEffect, useMemo, useState } from "react";
import { usePostAggregatePizzas } from "@/api/pizza-checker/pizzas/pizzas";
import { useBranch } from "@/context/branch-context";
import { translate } from "@/i18n/translate";

export interface MonthBounds {
  year: number;
  month: number;
  start: Date;
  end: Date;
}

export interface BillingBranchCapture {
  branchId: string;
  branchName: string;
  bbBranchId: string;
  captureCount: number;
}

export const buildMonthBounds = (
  year: number,
  monthIndexZeroBased: number,
): MonthBounds => {
  const start = new Date(Date.UTC(year, monthIndexZeroBased, 1, 0, 0, 0, 0));
  const end = new Date(
    Date.UTC(year, monthIndexZeroBased + 1, 0, 23, 59, 59, 999),
  );
  return { year, month: monthIndexZeroBased, start, end };
};

export const useBillingCaptureCounts = (
  bounds: MonthBounds | null,
  selectedBranchIds: string[],
) => {
  const { branches } = useBranch();
  const { mutateAsync, isPending } = usePostAggregatePizzas({});
  const [counts, setCounts] = useState<BillingBranchCapture[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [requestId, setRequestId] = useState(0);

  useEffect(() => {
    if (!bounds) {
      setCounts([]);
      return;
    }

    let cancelled = false;
    setError(null);

    const pipeline = [
      {
        $match: {
          capture_at: {
            $gte: bounds.start.toISOString(),
            $lte: bounds.end.toISOString(),
          },
        },
      },
      {
        $group: {
          _id: "$branch_id",
          captureCount: { $sum: 1 },
        },
      },
    ];

    mutateAsync({ data: { pipeline } })
      .then((response) => {
        if (cancelled) {
          return;
        }
        const results = (response?.results ?? []) as Array<{
          _id?: unknown;
          captureCount?: unknown;
        }>;
        const byBranchId = new Map<string, number>();
        for (const row of results) {
          if (typeof row._id === "string") {
            byBranchId.set(
              row._id,
              typeof row.captureCount === "number" ? row.captureCount : 0,
            );
          }
        }
        const next: BillingBranchCapture[] = (branches ?? [])
          .filter((branch) => branch.bb_branch_id)
          .map((branch) => ({
            branchId: branch.id,
            branchName: branch.name,
            bbBranchId: branch.bb_branch_id,
            captureCount: byBranchId.get(branch.bb_branch_id) ?? 0,
          }));
        setCounts(next);
        setRequestId((id) => id + 1);
      })
      .catch((err: unknown) => {
        if (cancelled) {
          return;
        }
        console.error("Billing capture count fetch failed", err);
        setError(
          err instanceof Error && err.message
            ? translate("billing.loadFailedReason", { reason: err.message })
            : translate("billing.loadFailed"),
        );
        setCounts([]);
      });

    return () => {
      cancelled = true;
    };
    // We intentionally re-fetch when the underlying branches list changes so newly
    // added branches appear in the table immediately.
  }, [bounds, branches, mutateAsync]);

  const visibleCounts = useMemo(() => {
    if (selectedBranchIds.length === 0) {
      return counts;
    }
    const selectedSet = new Set(selectedBranchIds);
    return counts.filter((entry) => selectedSet.has(entry.branchId));
  }, [counts, selectedBranchIds]);

  const totalCaptures = useMemo(
    () => visibleCounts.reduce((sum, entry) => sum + entry.captureCount, 0),
    [visibleCounts],
  );

  const refetch = useCallback(() => {
    setRequestId((id) => id + 1);
  }, []);

  return {
    captures: visibleCounts,
    totalCaptures,
    isLoading: isPending,
    error,
    requestId,
    refetch,
  };
};